import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import PayInvoiceModal from '../components/PayInvoiceModal';
import useWindowWidth from '../hooks/useWindowWidth';
import { swalError } from '../utils/alert';

function InvoiceDetailPage() {
    const { id } = useParams();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showPayModal, setShowPayModal] = useState(false);
    const width = useWindowWidth();

    useEffect(() => {
        api.get(`/invoices/${id}`)
            .then(response => {
                setInvoice(response.data);
                setLoading(false);
            })
            .catch(err => {
                console.error('Error fetching invoice details:', err);
                swalError('Falha ao carregar detalhes da fatura.');
                setError('Falha ao carregar detalhes da fatura.');
                setLoading(false);
            });
    }, [id]);

    const handlePaymentSuccess = (updatedInvoice) => {
        setInvoice(updatedInvoice);
        setShowPayModal(false);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    }

    if (loading) {
        return <div>Carregando detalhes da fatura...</div>;
    }

    if (error) {
        return <div>Falha ao carregar detalhes da fatura.</div>;
    }

    if (!invoice) {
        return <div>Fatura não encontrada.</div>;
    }

    const renderExpensesMobile = () => (
        <div>
            {invoice.expenses && invoice.expenses.map(expense => (
                <div className="card mb-2" key={expense.id}>
                    <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                            <strong style={{ fontSize: '0.95em' }}>{expense.description}</strong>
                            <span className="text-primary fw-bold">{formatCurrency(expense.amount)}</span>
                        </div>
                        <div className="small text-muted">
                            {formatDate(expense.due_date)}
                            {expense.installment_number && ` — Parcela ${expense.installment_number}/${expense.total_installments}`}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderExpensesDesktop = () => (
        <table className="table table-striped table-hover">
            <thead className="thead-dark">
                <tr>
                    <th>Data da Compra</th>
                    <th>Descrição</th>
                    <th>Parcela</th>
                    <th>Valor</th>
                </tr>
            </thead>
            <tbody>
                {invoice.expenses && invoice.expenses.map(expense => (
                    <tr key={expense.id}>
                        <td>{formatDate(expense.due_date)}</td>
                        <td>{expense.description}</td>
                        <td>{expense.installment_number ? `${expense.installment_number}/${expense.total_installments}` : '-'}</td>
                        <td>{formatCurrency(expense.amount)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    return (
        <div className="container mt-4">
            <Link to="/invoices" className="btn btn-light mb-4">Voltar para Faturas</Link>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="mb-0" style={{ fontSize: width < 768 ? '1.3em' : undefined }}>Detalhes da Fatura</h2>
                {invoice.status !== 'Paga' && (
                    <button className="btn btn-success btn-sm" onClick={() => setShowPayModal(true)}>
                        Pagar Fatura
                    </button>
                )}
            </div>

            <PayInvoiceModal
                show={showPayModal}
                onClose={() => setShowPayModal(false)}
                invoice={invoice}
                onPaymentSuccess={handlePaymentSuccess}
            />

            <div className="card mb-4">
                <div className="card-header">
                    Fatura #{invoice.id} - {invoice.credit_card?.name}
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-6 col-md-3 mb-2"><strong>Referência:</strong><br />{formatDate(invoice.reference_month)}</div>
                        <div className="col-6 col-md-3 mb-2"><strong>Vencimento:</strong><br />{formatDate(invoice.due_date)}</div>
                        <div className="col-6 col-md-3 mb-2"><strong>Status:</strong><br /><span className={`badge bg-${invoice.status === 'Paga' ? 'success' : 'warning'}`}>{invoice.status}</span></div>
                        <div className="col-6 col-md-3 mb-2"><strong>Valor Total:</strong><br />{formatCurrency(invoice.total_amount)}</div>
                    </div>
                </div>
            </div>

            <h4>Lançamentos da Fatura</h4>
            {width < 768 ? renderExpensesMobile() : renderExpensesDesktop()}
        </div>
    );
}

export default InvoiceDetailPage;
